import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand} from '../contexts/brand-context'

const getHomepageSeoSeed = () => {
    if (typeof window === 'undefined') return Date.now().toString()
    const cached = sessionStorage.getItem('seoHomepageSeed')
    if (cached) return cached
    const seed = Date.now().toString()
    sessionStorage.setItem('seoHomepageSeed', seed)
    return seed
}

const EMPTY_SEO_DATA = {
    siteTitle: '',
    schemaMarkup: null,
    pageGraphTagValues: null,
    c_alternateURLs: null,
    canonical: ''
}

export const useHomepageSeo = (initialData) => {
    const {locale, site} = useMultiSite()
    const {brandId} = useBrand()
    const appOrigin = useAppOrigin()
    const querySeed = useMemo(getHomepageSeoSeed, [])
    const localeId = locale?.id
    const siteId = site?.id

    // Reject initialData if it was fetched for a different brand (SSR mismatch guard)
    const safeInitialData =
        initialData?._brand && brandId && initialData._brand !== brandId ? undefined : initialData

    return useQuery({
        queryKey: ['seoHomepage', localeId, siteId, brandId, querySeed],
        queryFn: async () => {
            // Call SSR endpoint that handles token server-side
            const response = await fetch(
                `${appOrigin}/seo-homepage?locale=${encodeURIComponent(localeId || '')}&siteId=${encodeURIComponent(siteId || '')}&c_brand=${encodeURIComponent(brandId || '')}`
            )

            if (response.ok) {
                return await response.json()
            }

            return EMPTY_SEO_DATA
        },
        enabled: Boolean(localeId) && Boolean(siteId) && Boolean(brandId),
        initialData: safeInitialData ?? undefined,
        initialDataUpdatedAt: safeInitialData ? Date.now() : undefined,
        staleTime: Infinity,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
    })
}
